/**
 * Community Social API helpers — profile handle, feed, create, reactions.
 */
import api from "../../Environment";

function unwrap(response) {
  return response && response.data ? response.data : response;
}

export async function fetchSocialProfile() {
  try {
    const body = unwrap(await api.postMethod("community/social/me", {}));
    if (body && body.success && body.data) {
      return { profile: body.data.profile || null, error: false };
    }
    return { profile: null, error: true };
  } catch (e) {
    return { profile: null, error: true };
  }
}

export async function fetchCommunityPublicProfile(handle) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/profile", {
        handle: handle || "",
      })
    );
    if (body && body.success && body.data) {
      return { profile: body.data.profile || null, error: false };
    }
    return {
      profile: null,
      error: true,
      message: (body && body.error_message) || "Unable to load profile",
    };
  } catch (e) {
    return { profile: null, error: true, message: "Unable to load profile" };
  }
}

export async function checkCommunityHandle(handle) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/handle/check", { handle })
    );
    if (body && body.success && body.data) {
      return { ...body.data, error: false };
    }
    return {
      available: false,
      handle: null,
      reason: (body && body.error_message) || "Unable to check handle",
      error: true,
    };
  } catch (e) {
    return {
      available: false,
      handle: null,
      reason: "Unable to check handle",
      error: true,
    };
  }
}

export async function saveCommunityHandle(handle) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/handle/save", { handle })
    );
    if (body && body.success && body.data) {
      return { profile: body.data.profile || null, error: false };
    }
    return {
      profile: null,
      error: true,
      message: (body && body.error_message) || "Unable to save handle",
    };
  } catch (e) {
    return { profile: null, error: true, message: "Unable to save handle" };
  }
}

export async function fetchSocialFeed(opts = {}) {
  const input = {};
  if (opts.page) input.page = opts.page;
  if (opts.per_page) input.per_page = opts.per_page;
  if (opts.user_id) input.user_id = opts.user_id;
  if (opts.context || opts.context_type) {
    input.context = opts.context || opts.context_type;
  }
  if (opts.creator_id) input.creator_id = opts.creator_id;
  if (opts.organization_id) input.organization_id = opts.organization_id;
  if (opts.project_id) input.project_id = opts.project_id;
  // My Feed uses ranked; entity pages pass context (chrono on BE).
  // Community Profile must use fetchProfileTimeline() — not this helper.
  if (opts.mode) input.mode = opts.mode;
  // FormData JSON-stringifies arrays; send a CSV so Laravel receives a clean list.
  // Cap matches backend (5000) so large ranked windows can be fully excluded.
  if (Array.isArray(opts.exclude_ids) && opts.exclude_ids.length) {
    input.exclude_ids = opts.exclude_ids
      .map((id) => Number(id))
      .filter((id) => id > 0)
      .slice(0, 5000)
      .join(",");
  } else if (typeof opts.exclude_ids === "string" && opts.exclude_ids.trim()) {
    input.exclude_ids = opts.exclude_ids.trim();
  }

  try {
    const body = unwrap(
      await api.postMethod("community/social/feed", input)
    );
    if (body && body.success && body.data) {
      return {
        posts: Array.isArray(body.data.posts) ? body.data.posts : [],
        pagination: body.data.pagination || null,
        context: body.data.context || null,
        ranking: body.data.ranking || null,
        error: false,
      };
    }
    return {
      posts: [],
      pagination: null,
      context: null,
      ranking: null,
      error: true,
    };
  } catch (e) {
    return {
      posts: [],
      pagination: null,
      context: null,
      ranking: null,
      error: true,
    };
  }
}

/**
 * Community Profile personal wall — dedicated endpoint.
 * Never ranked; never entity / discovery / followed mix.
 */
export async function fetchProfileTimeline(opts = {}) {
  const userId = Number(opts.user_id || 0);
  if (!userId) {
    return {
      posts: [],
      pagination: null,
      context: null,
      error: true,
      message: "user_id required",
    };
  }
  const input = {
    user_id: userId,
    page: opts.page || 1,
    per_page: opts.per_page || 20,
  };
  try {
    const body = unwrap(
      await api.postMethod("community/social/profile/posts", input)
    );
    if (body && body.success && body.data) {
      return {
        posts: Array.isArray(body.data.posts) ? body.data.posts : [],
        pagination: body.data.pagination || null,
        context: body.data.context || null,
        error: false,
      };
    }
    return {
      posts: [],
      pagination: null,
      context: null,
      error: true,
    };
  } catch (e) {
    return {
      posts: [],
      pagination: null,
      context: null,
      error: true,
    };
  }
}

export async function followCommunityEntity(type, id) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/follow", {
        followable_type: type,
        followable_id: id,
      })
    );
    if (body && body.success) {
      return { ...(body.data || {}), error: false };
    }
    return {
      following: false,
      error: true,
      message: (body && body.error_message) || "Unable to follow",
    };
  } catch (e) {
    return { following: false, error: true, message: "Unable to follow" };
  }
}

export async function unfollowCommunityEntity(type, id) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/unfollow", {
        followable_type: type,
        followable_id: id,
      })
    );
    if (body && body.success) {
      return { ...(body.data || {}), error: false };
    }
    return {
      following: true,
      error: true,
      message: (body && body.error_message) || "Unable to unfollow",
    };
  } catch (e) {
    return { following: true, error: true, message: "Unable to unfollow" };
  }
}

export async function fetchCommunityFollows(type = null) {
  const input = {};
  if (type) input.followable_type = type;
  try {
    const body = unwrap(
      await api.postMethod("community/social/follows", input)
    );
    if (body && body.success && body.data) {
      return {
        follows: body.data.follows || [],
        counts: body.data.counts || {},
        error: false,
      };
    }
    return { follows: [], counts: {}, error: true };
  } catch (e) {
    return { follows: [], counts: {}, error: true };
  }
}

/** Owner roster for entity workspace follower management. */
export async function fetchEntityFollowers({
  type,
  id,
  q = "",
  page = 1,
  per_page = 25,
} = {}) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/followers", {
        followable_type: type,
        followable_id: id,
        q,
        page,
        per_page,
      })
    );
    if (body && body.success && body.data) {
      return {
        followers: body.data.followers || [],
        total: body.data.total || 0,
        page: body.data.page || 1,
        error: false,
      };
    }
    return {
      followers: [],
      total: 0,
      error: true,
      message: (body && (body.error_message || body.error)) || "Failed",
    };
  } catch (e) {
    return { followers: [], total: 0, error: true, message: "Network error" };
  }
}

export async function removeEntityFollower({ type, id, userId } = {}) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/followers/remove", {
        followable_type: type,
        followable_id: id,
        user_id: userId,
      })
    );
    if (body && body.success) {
      return { error: false, data: body.data };
    }
    return {
      error: true,
      message: (body && (body.error_message || body.error)) || "Failed",
    };
  } catch (e) {
    return { error: true, message: "Network error" };
  }
}

export async function createSocialPost(payload) {
  try {
    const input = {
      body: payload.body || payload.content || "",
      organization_id: payload.organization_id,
      creator_id: payload.creator_id,
      project_id: payload.project_id,
    };
    if (payload.is_prayer_request) {
      input.is_prayer_request = 1;
    }
    if (Array.isArray(payload.media) && payload.media.length) {
      input.media = payload.media;
    } else if (payload.media) {
      input.media = payload.media;
    } else if (payload.image) {
      input.media = payload.image;
    } else if (payload.video) {
      input.media = payload.video;
    }
    if (payload.gifs) {
      input.gifs = payload.gifs;
    } else if (payload.gif_url) {
      input.gif_url = payload.gif_url;
      input.gif_id = payload.gif_id;
      input.gif_provider = payload.gif_provider;
      input.gif_preview_url = payload.gif_preview_url;
      input.gif_width = payload.gif_width;
      input.gif_height = payload.gif_height;
    }
    const body = unwrap(
      await api.postMethod("community/social/post/create", input)
    );
    if (body && body.success && body.data && body.data.post) {
      return { post: body.data.post, error: false };
    }
    return {
      post: null,
      error: true,
      message: (body && body.error_message) || "Unable to create post",
    };
  } catch (e) {
    return { post: null, error: true, message: "Unable to create post" };
  }
}

export async function fetchCommunitySettings() {
  try {
    const body = unwrap(await api.postMethod("community/social/settings", {}));
    if (body && body.success && body.data) {
      return { settings: body.data.settings || null, error: false };
    }
    return { settings: null, error: true };
  } catch (e) {
    return { settings: null, error: true };
  }
}

export async function searchCommunityGifs(query, opts = {}) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/gifs/search", {
        q: query || "",
        limit: opts.limit || 24,
      })
    );
    if (body && body.success && body.data) {
      return { ...body.data, error: false };
    }
    return { results: [], configured: false, stub: true, error: true };
  } catch (e) {
    return { results: [], configured: false, stub: true, error: true };
  }
}

export async function saveCommunityProfile(payload = {}) {
  try {
    const input = {};
    if (payload.display_name != null) input.display_name = payload.display_name;
    if (payload.clear_avatar) input.clear_avatar = 1;
    if (payload.clear_cover) input.clear_cover = 1;
    if (payload.avatar) input.avatar = payload.avatar;
    if (payload.cover) input.cover = payload.cover;
    const body = unwrap(
      await api.postMethod("community/social/profile/save", input)
    );
    if (body && body.success && body.data) {
      return { profile: body.data.profile || null, error: false };
    }
    return {
      profile: null,
      error: true,
      message: (body && body.error_message) || "Unable to save profile",
    };
  } catch (e) {
    return { profile: null, error: true, message: "Unable to save profile" };
  }
}

export async function toggleSaveSocialPost(postId) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/post/save", { post_id: postId })
    );
    if (body && body.success && body.data) {
      return {
        saved: !!body.data.saved,
        post: body.data.post || null,
        error: false,
      };
    }
    return {
      saved: false,
      post: null,
      error: true,
      message: (body && body.error_message) || "Unable to save post",
    };
  } catch (e) {
    return { saved: false, post: null, error: true, message: "Unable to save post" };
  }
}

export async function fetchSocialComments(postId) {
  const id = Number(postId || 0);
  if (!id) {
    return { comments: [], total: 0, error: true, message: "post_id required" };
  }
  try {
    const body = unwrap(
      await api.postMethod("community/social/post/comments", {
        post_id: id,
      })
    );
    if (body && body.success && body.data) {
      return {
        comments: Array.isArray(body.data.comments) ? body.data.comments : [],
        total: body.data.total || 0,
        comment_count: body.data.comment_count,
        comments_locked: !!body.data.comments_locked,
        error: false,
      };
    }
    return {
      comments: [],
      total: 0,
      error: true,
      message: (body && body.error_message) || "Unable to load comments",
    };
  } catch (e) {
    return {
      comments: [],
      total: 0,
      error: true,
      message: "Unable to load comments",
    };
  }
}

export async function createSocialComment(postId, text, mediaFile = null) {
  try {
    const input = {
      post_id: postId,
      body: text || "",
    };
    if (mediaFile) {
      input.media = mediaFile;
    }
    const body = unwrap(
      await api.postMethod("community/social/post/comment", input)
    );
    if (body && body.success && body.data) {
      return { ...(body.data || {}), error: false };
    }
    return {
      error: true,
      message: (body && body.error_message) || "Unable to comment",
    };
  } catch (e) {
    return { error: true, message: "Unable to comment" };
  }
}

export async function deleteSocialComment(commentId) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/post/comment/delete", {
        comment_id: commentId,
      })
    );
    if (body && body.success && body.data) {
      return { ...(body.data || {}), error: false };
    }
    return {
      error: true,
      message: (body && body.error_message) || "Unable to delete comment",
    };
  } catch (e) {
    return { error: true, message: "Unable to delete comment" };
  }
}

export async function reactToSocialComment(
  commentId,
  reaction = "like",
  prayerTypeId = null,
  reactionTypeId = null
) {
  const payload = {
    comment_id: commentId,
    reaction,
  };
  if (prayerTypeId != null) {
    payload.prayer_type_id = prayerTypeId;
    payload.reaction = "prayer";
  }
  if (reactionTypeId != null) {
    payload.reaction_type_id = reactionTypeId;
    payload.reaction = "like";
  }
  try {
    const body = unwrap(
      await api.postMethod("community/social/post/comment/react", payload)
    );
    if (body && body.success && body.data) {
      return { comment: body.data.comment || null, error: false };
    }
    return {
      comment: null,
      error: true,
      message: (body && body.error_message) || "Unable to react",
    };
  } catch (e) {
    return { comment: null, error: true, message: "Unable to react" };
  }
}

export async function fetchSavedSocialPosts(opts = {}) {
  const input = {};
  if (opts.page) input.page = opts.page;
  if (opts.per_page) input.per_page = opts.per_page;
  try {
    const body = unwrap(
      await api.postMethod("community/social/saved", input)
    );
    if (body && body.success && body.data) {
      return {
        posts: Array.isArray(body.data.posts) ? body.data.posts : [],
        pagination: body.data.pagination || null,
        error: false,
      };
    }
    return { posts: [], pagination: null, error: true };
  } catch (e) {
    return { posts: [], pagination: null, error: true };
  }
}

export async function reactToSocialPost(
  postId,
  reaction = "like",
  prayerTypeId = null,
  reactionTypeId = null
) {
  try {
    const payload = {
      post_id: postId,
      reaction,
    };
    if (prayerTypeId != null) {
      payload.prayer_type_id = prayerTypeId;
      payload.reaction = "prayer";
    }
    if (reactionTypeId != null) {
      payload.reaction_type_id = reactionTypeId;
      payload.reaction = "like";
    }
    const body = unwrap(
      await api.postMethod("community/social/post/react", payload)
    );
    if (body && body.success && body.data && body.data.post) {
      return { post: body.data.post, error: false };
    }
    return {
      post: null,
      error: true,
      message: (body && body.error_message) || "Unable to react",
    };
  } catch (e) {
    return { post: null, error: true, message: "Unable to react" };
  }
}

export async function fetchCommunityPrayerTypes() {
  try {
    const body = unwrap(
      await api.postMethod("community/social/prayer-types", {})
    );
    if (body && body.success && body.data) {
      return {
        prayer_types: body.data.prayer_types || [],
        error: false,
      };
    }
    return { prayer_types: [], error: true, message: "Unable to load Prayer Types" };
  } catch (e) {
    return { prayer_types: [], error: true, message: "Unable to load Prayer Types" };
  }
}

export async function fetchCommunityReactionTypes() {
  try {
    const body = unwrap(
      await api.postMethod("community/social/reaction-types", {})
    );
    if (body && body.success && body.data) {
      return {
        reaction_types: body.data.reaction_types || [],
        error: false,
      };
    }
    return {
      reaction_types: [],
      error: true,
      message: "Unable to load Reaction Types",
    };
  } catch (e) {
    return {
      reaction_types: [],
      error: true,
      message: "Unable to load Reaction Types",
    };
  }
}

export async function updateSocialPost(postId, bodyText) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/post/update", {
        post_id: postId,
        body: bodyText,
      })
    );
    if (body && body.success && body.data && body.data.post) {
      return { post: body.data.post, error: false };
    }
    return {
      post: null,
      error: true,
      message: (body && body.error_message) || "Unable to update post",
    };
  } catch (e) {
    return { post: null, error: true, message: "Unable to update post" };
  }
}

export async function deleteSocialPost(postId) {
  try {
    const body = unwrap(
      await api.postMethod("community/social/post/delete", {
        post_id: postId,
      })
    );
    if (body && body.success) {
      return { deleted: true, id: postId, error: false };
    }
    return {
      deleted: false,
      error: true,
      message: (body && body.error_message) || "Unable to delete post",
    };
  } catch (e) {
    return { deleted: false, error: true, message: "Unable to delete post" };
  }
}

/**
 * @deprecated P0 — Do not use in production Social feeds.
 * Kept for local demos only; CommunitySocialLayout no longer calls this on error/empty.
 */
export function buildPlaceholderFeedPosts({ organizations = [], creators = [], projects = [] } = {}) {
  const now = Date.now();
  const posts = [];
  const org = organizations[0];
  const creator = creators[0];
  const project = projects[0];
  const org2 = organizations[1] || organizations[0];

  if (creator) {
    posts.push({
      id: `placeholder-creator-${creator.id || "1"}`,
      kind: "post",
      body:
        "Grateful for everyone walking this formation journey with us. New teaching drops this week — stay close.",
      content:
        "Grateful for everyone walking this formation journey with us. New teaching drops this week — stay close.",
      created_at: new Date(now - 1000 * 60 * 45).toISOString(),
      timestamp: "45m ago",
      author: {
        display_name: creator.name || creator.display_name || "Creator",
        handle: creator.slug ? creator.slug.replace(/[^a-z0-9_]/gi, "").slice(0, 24) : "creator",
        avatar: creator.avatar || creator.logo_path,
        path: creator.path || creator.public_path || `/creator/${creator.slug}`,
      },
      creator: {
        name: creator.name || creator.display_name,
        path: creator.path || creator.public_path || `/creator/${creator.slug}`,
        slug: creator.slug,
      },
      organization: org
        ? {
            name: org.name,
            path: org.path || org.public_path,
            logo_path: org.logo_path,
          }
        : null,
      project: null,
      counts: { like: 24, comment: 3, prayer: 11 },
      viewer_reactions: [],
      actions: ["like", "comment", "prayer"],
      _placeholder: true,
    });
  }

  if (org) {
    posts.push({
      id: `placeholder-org-${org.id || "1"}`,
      kind: "post",
      body:
        "Join us this Sunday for worship and fellowship. All are welcome — bring a friend and share the hope we carry.",
      content:
        "Join us this Sunday for worship and fellowship. All are welcome — bring a friend and share the hope we carry.",
      created_at: new Date(now - 1000 * 60 * 60 * 3).toISOString(),
      timestamp: "3h ago",
      author: {
        display_name: org.name,
        handle: (org.slug || "ministry").replace(/[^a-z0-9_]/gi, "").slice(0, 24),
        avatar: org.logo_path,
        path: org.path || org.public_path,
      },
      creator: null,
      organization: {
        name: org.name,
        path: org.path || org.public_path,
        logo_path: org.logo_path,
      },
      project: project
        ? {
            name: project.name || project.title,
            path: project.path || project.public_path || `/project/${project.slug}`,
          }
        : null,
      counts: { like: 58, comment: 7, prayer: 21 },
      viewer_reactions: [],
      actions: ["like", "comment", "prayer"],
      _placeholder: true,
    });
  }

  if (project) {
    posts.push({
      id: `placeholder-project-${project.id || "1"}`,
      kind: "post",
      body:
        "Milestone update: the team hit a new funding goal. Thank you for praying and giving — next phase starts soon.",
      content:
        "Milestone update: the team hit a new funding goal. Thank you for praying and giving — next phase starts soon.",
      created_at: new Date(now - 1000 * 60 * 60 * 26).toISOString(),
      timestamp: "1d ago",
      author: {
        display_name: (org2 && org2.name) || "Project team",
        handle: ((org2 && org2.slug) || "team").replace(/[^a-z0-9_]/gi, "").slice(0, 24),
        avatar: (org2 && org2.logo_path) || null,
        path: (org2 && (org2.path || org2.public_path)) || "/community/projects",
      },
      creator: null,
      organization: org2
        ? {
            name: org2.name,
            path: org2.path || org2.public_path,
            logo_path: org2.logo_path,
          }
        : null,
      project: {
        name: project.name || project.title,
        path: project.path || project.public_path || `/project/${project.slug}`,
      },
      counts: { like: 41, comment: 5, prayer: 33 },
      viewer_reactions: [],
      actions: ["like", "comment", "prayer"],
      _placeholder: true,
    });
  }

  return posts;
}
